import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronLeft, LogIn, Radio, RefreshCw } from 'lucide-react'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useSession } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { createCoverageFeed, getCoverageEvent, listCoverageEvents } from './api'
import { useEventFeeds } from './useEventFeeds'
import { BroadcastPanel } from './BroadcastPanel'
import { eventTypeLabel, placeOf } from './vocab'
import { explain } from '@/shared/api/problem'
import type { CoverageEventResponse, CoverageFeedResponse } from '@/shared/api/types'

/**
 * The phone page.
 *
 * Deliberately NOT inside the app shell. Somebody is standing in a crowd holding
 * a phone in one hand: a camera preview, one big button, and nothing else. A
 * sidebar, a breadcrumb and a global search box are all things they will hit by
 * accident.
 *
 * Reached at `/broadcast?feed=<id>` from the copied link, or bare at `/broadcast`
 * to pick on the device.
 */
export function BroadcastStandalonePage() {
  const [params, setParams] = useSearchParams()
  const wanted = params.get('feed')
  const wantedEvent = params.get('event')
  const user = useSession((s) => s.user)
  const status = useSession((s) => s.status)
  const [tick, setTick] = useState(0)
  const [chosen, setChosen] = useState<{ event: CoverageEventResponse; feed: CoverageFeedResponse } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, error: loadError, loading } = useLoad(
    () => (user ? listCoverageEvents({}, 0, 100) : Promise.resolve(null)),
    [user?.id ?? '', tick],
  )

  // ENDED stays offerable — "ended" covers a flat battery and a closed tab as
  // well as "the rally finished", and the server decides. CANCELLED is the one
  // status that means somebody decided this is not happening.
  const events = useMemo(
    () => (data?.content ?? []).filter((e) => e.statusCode !== 'CANCELLED'),
    [data],
  )

  // `clear()` and not `setChosen(null)` alone: `linked` is derived from the URL,
  // so leaving `?feed=` in place makes Back a no-op — and arriving from a copied
  // link is this page's PRIMARY entry path, which would strand whoever is holding
  // the phone on one camera until they hand-edit the address bar.
  const clear = () => {
    setChosen(null)
    const sp = new URLSearchParams(params)
    sp.delete('feed')
    sp.delete('event')
    setParams(sp, { replace: true })
  }

  /**
   * The copied link carries `&event=` because the list endpoint sends no feeds —
   * see `useEventFeeds`. This loads that one event; the scan below is the
   * fallback for links minted before that change.
   */
  const detail = useLoad(
    () => (user && wanted && wantedEvent ? getCoverageEvent(wantedEvent) : Promise.resolve(null)),
    [user?.id ?? '', wanted, wantedEvent, tick],
  )

  const linked = useMemo(() => {
    if (!wanted || chosen) return chosen
    const fromDetail = detail.data?.feeds.find((f) => f.id === wanted)
    if (fromDetail && detail.data) return { event: detail.data, feed: fromDetail }
    for (const e of events) {
      const f = e.feeds.find((x) => x.id === wanted)
      if (f) return { event: e, feed: f }
    }
    return null
  }, [wanted, chosen, events, detail.data])

  const resolving = Boolean(wanted && wantedEvent && detail.loading && !detail.data)

  if (status === 'unknown') return <Splash label="Checking your session…" />

  if (!user) {
    const next = encodeURIComponent(window.location.pathname + window.location.search)
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <div className="max-w-sm space-y-3 text-center">
          <Radio className="mx-auto h-8 w-8 text-chocolate" />
          <h1 className="text-erp-lg font-semibold">Sign in to broadcast</h1>
          <p className="text-erp-sm text-muted-foreground">
            Use the same account you use in Axis. The broadcast is attributed to you.
          </p>
          <Link className="erp-btn erp-btn-primary h-11 px-5" to={`/login?next=${next}`}>
            <LogIn className="h-4 w-4" /> Sign in
          </Link>
        </div>
      </div>
    )
  }

  const active = linked ?? chosen

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b border-border bg-background px-3">
        {active ? (
          <button type="button" className="erp-btn h-8" aria-label="Back to the event list"
            onClick={clear}>
            <ChevronLeft className="h-4 w-4" />
          </button>
        ) : (
          <span className="grid h-7 w-7 place-items-center rounded bg-chocolate font-mono text-erp-xs font-bold text-white">
            A
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-erp-sm font-semibold">
            {active ? active.event.title : 'Field broadcast'}
          </div>
          <div className="-mt-0.5 truncate text-erp-xs text-muted-foreground">
            {active ? active.feed.title : user.name}
          </div>
        </div>
        {!active && (
          <button type="button" className="erp-btn h-8" aria-label="Refresh"
            onClick={() => setTick((t) => t + 1)}>
            <RefreshCw className="h-4 w-4" />
          </button>
        )}
      </header>

      <main className="space-y-3 p-3">
        <ErrorStrip message={error ?? loadError} />

        {/*
          Gated on `data` having arrived, not on `!loading`. With no user yet
          `useLoad` resolves immediately, so on the render where the session
          appears — deps changed, effect not yet run — `loading` is false and
          `linked` is null, and this would flash "your link is invalid" at
          somebody standing in a crowd before anything had been fetched.
        */}
        {wanted && !linked && !resolving && data !== null && (
          <Note tone="bad">
            That link does not match any camera you can see. Pick one below instead.
          </Note>
        )}

        {resolving ? <Splash label="Finding that camera…" /> : active ? (
          <BroadcastPanel
            feedId={active.feed.id}
            feedTitle={active.feed.title}
            eventTitle={active.event.title}
            onEnded={() => { clear(); setTick((t) => t + 1) }}
          />
        ) : loading ? <Splash label="Loading events…" /> : (
          <EventPicker
            events={events}
            onPick={(event, feed) => {
              setChosen({ event, feed })
              const sp = new URLSearchParams(params)
              sp.set('feed', feed.id)
              sp.set('event', event.id)
              setParams(sp, { replace: true })
            }}
            onError={setError}
            onCreated={() => setTick((t) => t + 1)}
          />
        )}
      </main>
    </div>
  )
}

function EventPicker({
  events, onPick, onError, onCreated,
}: {
  events: CoverageEventResponse[]
  onPick: (e: CoverageEventResponse, f: CoverageFeedResponse) => void
  onError: (m: string | null) => void
  onCreated: () => void
}) {
  const [busy, setBusy] = useState<string | null>(null)
  // The list carries no feeds; without this the phone offers "+ New camera" and
  // nothing else, so somebody standing at a rally with a camera already booked
  // for them has to create a second one.
  const slots = useEventFeeds(events, 8)

  if (events.length === 0) {
    return (
      <div className="py-10 text-center">
        <Radio className="mx-auto h-7 w-7 text-muted-foreground" />
        <p className="mt-2 text-erp-sm font-medium">No open events</p>
        <p className="text-erp-xs text-muted-foreground">
          Somebody at the desk needs to create one in Live Coverage first.
        </p>
      </div>
    )
  }

  return (
    <ul className="space-y-2">
      {events.slice(0, slots.cap).map((e) => {
        const known = slots.feeds.get(e.id)
        // Ended cameras are offered too — the button says "· again".
        const open = known ?? []
        return (
          <li key={e.id} className="erp-card space-y-2 p-3">
            <div>
              <div className="text-erp-sm font-semibold">{e.title}</div>
              <div className="text-erp-xs text-muted-foreground">
                {eventTypeLabel(e.eventTypeCode)} · {placeOf(e)}
              </div>
              <div className="text-erp-xs text-muted-foreground">
                {new Date(e.scheduledStart).toLocaleString()}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {!known && slots.loading && (
                <span className="self-center text-erp-xs text-muted-foreground">
                  Loading cameras…
                </span>
              )}
              {open.map((f) => {
                const live = f.statusCode === 'LIVE'
                return (
                  <button
                    key={f.id}
                    type="button"
                    className="erp-btn h-11"
                    disabled={live}
                    onClick={() => onPick(e, f)}
                  >
                    <Radio className="h-4 w-4" />
                    {f.title}
                    {/*
                      Naming the holder BEFORE the tap. The server refuses a
                      publisher token for a camera another account holds, and it
                      is right to — but discovering that after walking into a
                      crowd and pressing a button is the wrong moment.
                    */}
                    {live ? ' · in use'
                      : f.statusCode === 'ENDED' ? ' · again'
                      : f.broadcasterName ? ` · ${f.broadcasterName}` : ''}
                  </button>
                )
              })}
              <button
                type="button"
                className="erp-btn erp-btn-primary h-11"
                disabled={busy === e.id}
                onClick={async () => {
                  setBusy(e.id); onError(null)
                  try {
                    const feed = await createCoverageFeed(e.id, {
                    title: `Phone ${(known?.length ?? 0) + 1}`,
                  })
                    onCreated()
                    onPick(e, feed)
                  } catch (err) { onError(explain(err)) } finally { setBusy(null) }
                }}
              >
                + New camera
              </button>
            </div>
          </li>
        )
      })}
      {slots.truncated && (
        <li className="px-1 py-2 text-erp-xs text-muted-foreground">
          Showing the first {slots.cap} open events.
        </li>
      )}
    </ul>
  )
}

function Splash({ label }: { label: string }) {
  return <div className="grid min-h-dvh place-items-center"><LoadingBlock label={label} /></div>
}
